import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import {
  api,
  createQueryKeys,
  type ListNotificationsResponse,
  notificationEndpoints,
} from '@shared/api';

import type { Notification } from '../model/notification';
import { toNotification } from './notification.mapper';

export const notificationKeys = createQueryKeys('notification');

/** 한 쪽의 건수 */
export const NOTIFICATION_PAGE_SIZE = 6;

export type NotificationPages = InfiniteData<ListNotificationsResponse, string | null>;

export interface NotificationFeed {
  items: Notification[];
  hasUnread: boolean;
  unreadCount: number;
}

/**
 * 헤더 알림(커서 페이지). 빨간 점과 목록이 같은 캐시를 본다 — 레거시처럼 두 번 부르지 않고,
 * 목록에서 읽음 처리하면 점도 함께 바뀐다.
 * 빨간 점은 받은 쪽 안에서만 본다(레거시와 같다 — 안 읽은 개수 API 없음).
 */
export function useNotificationFeedQuery(options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: notificationKeys.lists(),
    queryFn: ({ pageParam, signal }) =>
      api.request(notificationEndpoints.listNotifications, {
        query: { size: NOTIFICATION_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    select: (data): NotificationFeed => {
      const items = data.pages.flatMap((page) => page.items.map(toNotification));
      const unreadCount = data.pages[0]?.unreadCount ?? 0;
      return { items, hasUnread: unreadCount > 0, unreadCount };
    },
  });
}
